// src/pages/People.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Divider,
  Card,
  CardContent,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { userAPI, reportingAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format, formatDistanceToNow } from 'date-fns';
import LABELS from '../config/terminology';

// Phone number formatting for input fields
const formatPhoneInput = (value) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length === 0) return '';
  if (numbers.length <= 3) return `(${numbers}`;
  if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
};

// Format phone for display (e.g. "***-***-1234" → "(***) ***-1234", or "+15551234567" → "(555) 123-4567")
const formatPhoneDisplay = (phone) => {
  if (!phone) return 'Unknown';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return phone; // fallback to raw value (e.g. masked "***-***-1234")
};

// Display name for a client, falling back to phone or email
const clientDisplayName = (client) => client.name || formatPhoneDisplay(client.phone) || client.email || 'Unknown';

const People = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const isSelfIncluded = user?.accountType === 'self';
  const [clients, setClients] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [clientsStats, setClientsStats] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '' });
  const [formError, setFormError] = useState('');
  const [phoneFieldError, setPhoneFieldError] = useState('');
  const [emailFieldError, setEmailFieldError] = useState('');
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const phoneCheckController = useRef(null);
  const emailCheckController = useRef(null);

  // Delete dialog
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Resend dialog
  const [resendingId, setResendingId] = useState(null);

  // Cancel verification dialog
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [cancelDowngradeInfo, setCancelDowngradeInfo] = useState(null);

  // Edit dialog
  const [editClient, setEditClient] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Send guide
  const [sendingGuide, setSendingGuide] = useState(null);

  // Upgrade confirmation
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);

  // Downgrade confirmation (removal)
  const [downgradeInfo, setDowngradeInfo] = useState(null);

  // Self-enrollment
  const [addingSelf, setAddingSelf] = useState(false);
  const [selfUpgradeInfo, setSelfUpgradeInfo] = useState(null);
  const [removeSelfConfirm, setRemoveSelfConfirm] = useState(false);
  const [selfDowngradeInfo, setSelfDowngradeInfo] = useState(null);
  const [removingSelf, setRemovingSelf] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [clientsRes, statsRes, subRes] = await Promise.all([
        userAPI.getClients(),
        reportingAPI.getClientsStats().catch(() => ({ data: { clients: [] } })),
        userAPI.getSubscription().catch(() => ({ data: { subscription: null } })),
      ]);

      setClients(clientsRes.data.clients || []);
      setPendingVerifications(clientsRes.data.pendingVerifications || []);
      setSubscription(subRes.data.subscription);

      // Convert stats array to object keyed by client ID
      const statsMap = {};
      (statsRes.data.clients || []).forEach(stat => {
        statsMap[stat.client_id] = stat;
      });
      setClientsStats(statsMap);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setFormData({ name: '', phone: '', email: '' });
    setFormError('');
    setPhoneFieldError('');
    setEmailFieldError('');
    setAddDialogOpen(true);
  };

  const handleAddClose = () => {
    setAddDialogOpen(false);
  };

  const handleFormChange = (e) => {
    let { name, value } = e.target;

    if (name === 'phone') {
      value = formatPhoneInput(value);
      setPhoneFieldError('');
    }
    if (name === 'email') {
      setEmailFieldError('');
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handlePhoneBlur = async () => {
    const digits = formData.phone.replace(/\D/g, '');
    if (digits.length < 10) return;

    if (phoneCheckController.current) phoneCheckController.current.abort();
    phoneCheckController.current = new AbortController();

    setCheckingPhone(true);
    setPhoneFieldError('');

    try {
      const formattedPhone = digits.length === 10 ? `+1${digits}` : `+${digits}`;
      const res = await userAPI.checkClientPhone(formattedPhone);
      if (!res.data.available) {
        setPhoneFieldError(res.data.message || 'This number is already enrolled.');
      }
    } catch (err) {
      if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
        console.error('Phone check error:', err);
      }
    } finally {
      setCheckingPhone(false);
    }
  };

  const handleEmailBlur = async () => {
    const email = formData.email.trim();
    if (!email || !email.includes('@')) return;

    if (emailCheckController.current) emailCheckController.current.abort();
    emailCheckController.current = new AbortController();

    setCheckingEmail(true);
    setEmailFieldError('');

    try {
      const res = await userAPI.checkClientEmail(email);
      if (!res.data.available) {
        setEmailFieldError(res.data.message || 'This email is already enrolled.');
      }
    } catch (err) {
      if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
        console.error('Email check error:', err);
      }
    } finally {
      setCheckingEmail(false);
    }
  };

  const submitAddClient = async (data) => {
    setSubmitting(true);
    setFormError('');

    try {
      const response = await userAPI.addClient(data);

      setAddDialogOpen(false);
      setUpgradeInfo(null);
      setPendingSubmitData(null);

      // Show success message with proration info if applicable
      const isEmailOnly = response.data.verification?.status === 'verified';
      if (response.data.proration) {
        setSuccess(isEmailOnly
          ? `Added successfully! ${response.data.proration.message}`
          : `Verification sent! ${response.data.proration.message}`);
      } else {
        setSuccess(isEmailOnly
          ? 'Added successfully! They can now forward suspicious emails to check@secondclear.com.'
          : 'Verification SMS sent! They need to reply YES to join.');
      }

      await loadData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error adding:', err);
      const code = err.response?.data?.code;

      if (code === 'UPGRADE_REQUIRED') {
        // Show upgrade confirmation dialog instead of error
        setUpgradeInfo(err.response.data.upgrade);
        setPendingSubmitData(data);
        return; // don't show as error
      }

      setFormError(err.response?.data?.error || 'An unexpected error occurred. Please try again or contact support at help@secondclear.com.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddSubmit = async () => {
    // Cancel any in-flight blur checks
    if (phoneCheckController.current) phoneCheckController.current.abort();
    if (emailCheckController.current) emailCheckController.current.abort();
    setCheckingPhone(false);
    setCheckingEmail(false);
    setPhoneFieldError('');
    setEmailFieldError('');
    setFormError('');

    if (!formData.name.trim()) {
      setFormError('Please enter a name');
      return;
    }

    const hasPhone = formData.phone.trim().length > 0;
    const hasEmail = formData.email.trim().length > 0;

    if (!hasPhone && !hasEmail) {
      setFormError('Please provide a phone number or email address (or both)');
      return;
    }

    let formattedPhone;
    if (hasPhone) {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        setFormError('Please enter a valid 10-digit phone number');
        return;
      }
      formattedPhone = phoneDigits.length === 10 ? `+1${phoneDigits}` : `+${phoneDigits}`;
    }

    if (hasEmail && !formData.email.trim().includes('@')) {
      setFormError('Please enter a valid email address');
      return;
    }

    // Show spinner immediately
    setSubmitting(true);

    // Run duplicate checks before submitting
    try {
      if (hasPhone) {
        const phoneRes = await userAPI.checkClientPhone(formattedPhone);
        if (!phoneRes.data.available) {
          setPhoneFieldError(phoneRes.data.message || 'This number is already enrolled.');
          setSubmitting(false);
          return;
        }
      }
      if (hasEmail) {
        const emailRes = await userAPI.checkClientEmail(formData.email.trim());
        if (!emailRes.data.available) {
          setEmailFieldError(emailRes.data.message || 'This email is already enrolled.');
          setSubmitting(false);
          return;
        }
      }
    } catch (err) {
      console.error('Validation check error:', err);
      // If checks fail (network etc), proceed anyway — backend will catch duplicates
    }

    // All clear — submit
    await submitAddClient({
      name: formData.name.trim(),
      phone: formattedPhone || undefined,
      email: hasEmail ? formData.email.trim() : undefined,
    });
  };

  const handleUpgradeConfirm = async () => {
    if (!pendingSubmitData) return;
    await submitAddClient({ ...pendingSubmitData, confirmUpgrade: true });
  };

  const handleUpgradeCancel = () => {
    setUpgradeInfo(null);
    setPendingSubmitData(null);
  };

  const handleDeleteClick = (client) => {
    setDeleteConfirm(client);
  };

  const handleDeleteConfirm = async (confirmDowngrade = false) => {
    if (!deleteConfirm) return;

    setDeleting(true);
    try {
      const body = confirmDowngrade ? { confirmDowngrade: true } : undefined;
      const response = await userAPI.removeClient(deleteConfirm.id, body);
      setDeleteConfirm(null);
      setDowngradeInfo(null);

      if (response.data.accountTypeReverted) {
        await refreshUser();
        navigate('/settings');
        return;
      }

      const removedName = clientDisplayName(deleteConfirm);
      if (response.data.proration) {
        setSuccess(`${removedName} removed. ${response.data.proration.message}`);
      } else {
        setSuccess(`${removedName} has been removed.`);
      }

      await loadData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      if (err.response?.data?.code === 'DOWNGRADE_PREVIEW') {
        setDowngradeInfo(err.response.data.downgrade);
      } else {
        console.error('Error removing:', err);
        setError(err.response?.data?.error || 'Failed to remove');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleResend = async (verification) => {
    setResendingId(verification.id);
    try {
      await userAPI.resendVerification(verification.id);
      setSuccess(`Verification SMS resent to ${verification.name || formatPhoneDisplay(verification.phone) || 'Unknown'}`);
      await loadData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error resending verification:', err);
      setError(err.response?.data?.error || 'Failed to resend verification');
    } finally {
      setResendingId(null);
    }
  };

  const handleCancelVerification = async (confirmDowngrade = false) => {
    if (!cancelConfirm) return;

    try {
      const response = await userAPI.cancelVerification(
        cancelConfirm.id,
        confirmDowngrade ? { confirmDowngrade: true } : undefined
      );
      setCancelConfirm(null);
      setCancelDowngradeInfo(null);

      if (response.data.accountTypeReverted) {
        await refreshUser();
        navigate('/settings');
        return;
      }

      setSuccess('Verification canceled');
      await loadData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      if (err.response?.data?.code === 'DOWNGRADE_PREVIEW') {
        setCancelDowngradeInfo(err.response.data.downgrade);
        return;
      }
      console.error('Error canceling verification:', err);
      setError('Failed to cancel verification');
    }
  };

  const handleEditClick = (client) => {
    setEditClient(client);
    setEditName(client.name || '');
    setEditEmail(client.email || '');
  };

  const handleEditSubmit = async () => {
    if (!editClient) return;

    // Basic email format check
    if (editEmail.trim() && !editEmail.trim().includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setEditSubmitting(true);
    try {
      await userAPI.updateClient(editClient.id, {
        name: editName,
        email: editEmail.trim() || null,
      });
      setEditClient(null);
      setSuccess('Updated successfully');
      await loadData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error updating:', err);
      setError(err.response?.data?.error || 'Failed to update');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleSendGuide = async (clientId) => {
    setSendingGuide(clientId);
    try {
      await userAPI.sendGuide(clientId);
      setSuccess('Quick start guide sent!');
      loadData(); // refresh to show updated guideSentAt
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error sending guide:', err);
      setError(err.response?.data?.error || 'Failed to send guide');
    } finally {
      setSendingGuide(null);
    }
  };

  const handleAddSelf = async (confirmUpgrade = false) => {
    setAddingSelf(true);
    setError('');

    try {
      const data = confirmUpgrade ? { confirmUpgrade: true } : {};
      const response = await userAPI.addSelf(data);

      setSelfUpgradeInfo(null);
      await refreshUser();
      await loadData();

      if (response.data.proration) {
        setSuccess(`You're now using Second Clear for yourself! ${response.data.proration.message}`);
      } else {
        setSuccess('You\'re now using Second Clear for yourself! Text or forward emails to get real analysis.');
      }
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      if (err.response?.data?.code === 'UPGRADE_REQUIRED') {
        setSelfUpgradeInfo(err.response.data.upgrade);
      } else {
        setError(err.response?.data?.error || 'Failed to add yourself. Please try again.');
      }
    } finally {
      setAddingSelf(false);
    }
  };

  const handleRemoveSelf = async (confirmDowngrade = false) => {
    setRemovingSelf(true);
    setError('');

    try {
      const data = confirmDowngrade ? { confirmDowngrade: true } : {};
      const response = await userAPI.removeSelf(data);

      setRemoveSelfConfirm(false);
      setSelfDowngradeInfo(null);
      await refreshUser();
      await loadData();

      if (response.data.proration) {
        setSuccess(`Removed yourself. ${response.data.proration.message}`);
      } else {
        setSuccess('You are no longer using Second Clear for yourself.');
      }
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      if (err.response?.data?.code === 'DOWNGRADE_PREVIEW') {
        setSelfDowngradeInfo(err.response.data.downgrade);
      } else {
        setError(err.response?.data?.error || 'Failed to remove yourself. Please try again.');
      }
    } finally {
      setRemovingSelf(false);
    }
  };

  const getStatusChip = (status, isExpired) => {
    if (status === 'verified') {
      return <Chip icon={<CheckCircleIcon />} label="Verified" color="success" size="small" />;
    }
    if (status === 'pending' && !isExpired) {
      return <Chip icon={<HourglassIcon />} label="Pending" color="warning" size="small" />;
    }
    if (isExpired || status === 'expired') {
      return <Chip icon={<AccessTimeIcon />} label="Expired" color="default" size="small" />;
    }
    if (status === 'declined') {
      return <Chip icon={<CancelIcon />} label="Declined" color="error" size="small" />;
    }
    return <Chip label={status} size="small" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const totalClients = clients.length + pendingVerifications.filter(p => p.verificationStatus === 'pending' && !p.isExpired).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{LABELS.client.tabLabel}</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
          >
            {LABELS.client.addButton}
          </Button>
        </Box>
      </Box>

      {/* Subscription Summary Card */}
      {subscription && (
        <Card sx={{ mb: 3, bgcolor: '#f5f7fa' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Subscription
                </Typography>
                <Typography variant="h6">
                  {subscription.quantity} {subscription.quantity === 1 ? 'person' : 'people'}
                  <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
                    (${(subscription.quantity * 15).toFixed(2)}/month)
                  </Typography>
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="body2" color="text.secondary">
                  Currently using: {subscription.currentUsage} of {subscription.quantity}
                </Typography>
                {subscription.availableSlots > 0 && (
                  <Typography variant="body2" color="success.main">
                    {subscription.availableSlots} slot{subscription.availableSlots !== 1 ? 's' : ''} available
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Self-Enrollment Card */}
      {user?.accountType === 'owner' && (
        <Card sx={{ mb: 3, border: '2px dashed', borderColor: 'primary.light', bgcolor: 'background.paper' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Use Second Clear for yourself
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Get real scam analysis when you text or forward emails — not just the demo response.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<PersonIcon />}
                onClick={() => handleAddSelf(false)}
                disabled={addingSelf}
              >
                {addingSelf ? <CircularProgress size={24} /> : 'Add Yourself'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {isSelfIncluded && (
        <Card sx={{ mb: 3, bgcolor: '#f0f7ff' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2}>
                <PersonIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {user?.name || 'You'} <Typography component="span" color="text.secondary">(Yourself)</Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    You're using Second Clear for your own messages
                  </Typography>
                </Box>
                <Chip label="Active" color="success" size="small" />
              </Box>
              <Tooltip title="Stop using the service for yourself">
                <IconButton
                  color="error"
                  onClick={() => setRemoveSelfConfirm(true)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Pending Verifications Section */}
      {pendingVerifications.length > 0 && (
        <Box mb={4}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HourglassIcon color="warning" />
            Pending Verifications
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Sent</TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Expires</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingVerifications.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell>{v.name || formatPhoneDisplay(v.phone) || v.email || 'Unknown'}</TableCell>
                    <TableCell>{v.phone || '-'}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{v.email || '-'}</TableCell>
                    <TableCell align="center">
                      {getStatusChip(v.verificationStatus, v.isExpired)}
                    </TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {v.smsSentAt ? formatDistanceToNow(new Date(v.smsSentAt), { addSuffix: true }) : '-'}
                    </TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {v.expiresAt && !v.isExpired
                        ? formatDistanceToNow(new Date(v.expiresAt), { addSuffix: true })
                        : '-'}
                    </TableCell>
                    <TableCell align="center">
                      {v.canResend && (
                        <Tooltip title="Resend verification SMS">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleResend(v)}
                            disabled={resendingId === v.id}
                          >
                            {resendingId === v.id ? <CircularProgress size={20} /> : <RefreshIcon />}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Cancel verification">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setCancelConfirm(v)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Linked People Section */}
      {clients.length === 0 && pendingVerifications.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {LABELS.client.emptyState}
          </Typography>
          <Typography color="text.secondary" paragraph>
            Add someone to start monitoring their messages for fraud attempts.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddClick}>
            {LABELS.client.addFirstPrompt}
          </Button>
        </Paper>
      ) : clients.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" />
            Verified ({clients.length})
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Email</TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Messages</TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>High Risk</TableCell>
                  <TableCell align="center">Last Activity</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((client) => {
                  const stats = clientsStats[client.id] || {};
                  return (
                    <TableRow key={client.id} hover sx={{ cursor: 'pointer' }}>
                      <TableCell onClick={() => navigate(`/messages?client_id=${client.id}`)}>
                        <Typography variant="body1">{clientDisplayName(client)}</Typography>
                      </TableCell>
                      <TableCell onClick={() => navigate(`/messages?client_id=${client.id}`)}>
                        {client.phone || '-'}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} onClick={() => navigate(`/messages?client_id=${client.id}`)}>
                        {client.email || '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }} onClick={() => navigate(`/messages?client_id=${client.id}`)}>
                        <Chip label={stats.total_messages || client.eventCount || 0} size="small" />
                      </TableCell>
                      <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }} onClick={() => navigate(`/messages?client_id=${client.id}`)}>
                        <Chip
                          label={stats.high_risk_count || 0}
                          color={stats.high_risk_count > 0 ? 'error' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center" onClick={() => navigate(`/messages?client_id=${client.id}`)}>
                        {stats.last_activity || client.lastActivity
                          ? format(new Date(stats.last_activity || client.lastActivity), 'MMM d, yyyy')
                          : 'No activity'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View messages">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/messages?client_id=${client.id}`)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit name">
                          <IconButton
                            size="small"
                            color="default"
                            onClick={() => handleEditClick(client)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={client.guideSentAt ? `Guide last sent ${format(new Date(client.guideSentAt), 'MMM d, yyyy')}` : 'Send quick start guide'}>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleSendGuide(client.id)}
                            disabled={sendingGuide === client.id || !client.phone}
                          >
                            {sendingGuide === client.id ? <CircularProgress size={20} /> : <MenuBookIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(client)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onClose={handleAddClose} maxWidth="sm" fullWidth>
        <DialogTitle>{LABELS.client.addTitle}</DialogTitle>
        <form onSubmit={(e) => { e.preventDefault(); handleAddSubmit(); }}>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a name and provide a phone number, email address, or both. If a phone number is provided, we'll send a verification text. Email-only entries are added immediately.
          </Typography>

          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}

          <TextField
            margin="normal"
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleFormChange}
            disabled={submitting}
            required
            placeholder="e.g., Mom, Dad, Grandma"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            margin="normal"
            fullWidth
            label="Phone Number (optional)"
            name="phone"
            placeholder="(555) 555-5555"
            value={formData.phone}
            onChange={handleFormChange}
            disabled={submitting}
            autoFocus
            inputProps={{ maxLength: 14 }}
            error={!!phoneFieldError}
            helperText={phoneFieldError || 'They will receive a verification text message'}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon color={phoneFieldError ? 'error' : 'action'} />
                </InputAdornment>
              ),
              endAdornment: checkingPhone ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : null,
            }}
          />

          <TextField
            margin="normal"
            fullWidth
            label="Email Address (optional)"
            name="email"
            type="email"
            placeholder="email@example.com"
            value={formData.email}
            onChange={handleFormChange}
            disabled={submitting}
            error={!!emailFieldError}
            helperText={emailFieldError || 'They can forward suspicious emails to check@secondclear.com'}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color={emailFieldError ? 'error' : 'action'} />
                </InputAdornment>
              ),
              endAdornment: checkingEmail ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : null,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !!phoneFieldError || !!emailFieldError}
          >
            {submitting ? <CircularProgress size={24} /> : (
              formData.phone.trim() ? 'Send Verification' : 'Add'
            )}
          </Button>
        </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => { setDeleteConfirm(null); setDowngradeInfo(null); }}>
        <DialogTitle>{downgradeInfo ? 'Confirm Subscription Change' : 'Remove this person?'}</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{deleteConfirm && clientDisplayName(deleteConfirm)}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            They will no longer be linked to your account, but can still use the service independently.
          </Typography>
          {downgradeInfo ? (
            <>
              <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
                {downgradeInfo.message}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f7fa' }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography color="text.secondary">Current plan:</Typography>
                  <Typography fontWeight="bold">
                    {downgradeInfo.currentQuantity} {downgradeInfo.currentQuantity === 1 ? 'person' : 'people'} — ${downgradeInfo.currentMonthly}/month
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">New plan:</Typography>
                  <Typography fontWeight="bold" color="success.main">
                    {downgradeInfo.newQuantity} {downgradeInfo.newQuantity === 1 ? 'person' : 'people'} — ${downgradeInfo.newMonthly}/month
                  </Typography>
                </Box>
              </Paper>
            </>
          ) : subscription && subscription.quantity > 1 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Your subscription may be adjusted and you'll receive a prorated credit.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteConfirm(null); setDowngradeInfo(null); }} disabled={deleting}>
            Cancel
          </Button>
          {downgradeInfo ? (
            <Button onClick={() => handleDeleteConfirm(true)} color="error" variant="contained" disabled={deleting}>
              {deleting ? <CircularProgress size={24} /> : 'Confirm & Remove'}
            </Button>
          ) : (
            <Button onClick={() => handleDeleteConfirm(false)} color="error" variant="contained" disabled={deleting}>
              {deleting ? <CircularProgress size={24} /> : 'Remove'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel Verification Dialog */}
      <Dialog open={!!cancelConfirm} onClose={() => { setCancelConfirm(null); setCancelDowngradeInfo(null); }}>
        <DialogTitle>{cancelDowngradeInfo ? 'Confirm Subscription Change' : 'Cancel Verification?'}</DialogTitle>
        <DialogContent>
          {cancelDowngradeInfo ? (
            <>
              <Typography sx={{ mb: 2 }}>
                Canceling the verification for <strong>{cancelConfirm && (cancelConfirm.name || formatPhoneDisplay(cancelConfirm.phone) || 'Unknown')}</strong> will also update your subscription:
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {cancelDowngradeInfo.message}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f7fa' }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography color="text.secondary">Current plan:</Typography>
                  <Typography fontWeight="bold">
                    {cancelDowngradeInfo.currentQuantity} {cancelDowngradeInfo.currentQuantity === 1 ? 'person' : 'people'} — ${cancelDowngradeInfo.currentMonthly}/month
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">New plan:</Typography>
                  <Typography fontWeight="bold" color="success.main">
                    {cancelDowngradeInfo.newQuantity} {cancelDowngradeInfo.newQuantity === 1 ? 'person' : 'people'} — ${cancelDowngradeInfo.newMonthly}/month
                  </Typography>
                </Box>
              </Paper>
            </>
          ) : (
            <>
              <Typography>
                Cancel the pending verification for <strong>{cancelConfirm && (cancelConfirm.name || formatPhoneDisplay(cancelConfirm.phone) || 'Unknown')}</strong>?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                They will not be added to your account. You can send a new verification later.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCancelConfirm(null); setCancelDowngradeInfo(null); }}>
            {cancelDowngradeInfo ? 'Keep Subscription' : 'Keep Waiting'}
          </Button>
          {cancelDowngradeInfo ? (
            <Button onClick={() => handleCancelVerification(true)} variant="contained">
              Confirm & Cancel
            </Button>
          ) : (
            <Button onClick={() => handleCancelVerification(false)} color="error" variant="contained">
              Cancel Verification
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editClient} onClose={() => setEditClient(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Update details for {editClient && (editClient.name || formatPhoneDisplay(editClient.phone) || editClient.email || 'this person')}.
          </Typography>
          <TextField
            margin="normal"
            fullWidth
            label="Name (optional)"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            disabled={editSubmitting}
            autoFocus
            placeholder="e.g., Mom, Dad, Grandma"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Email Address (optional)"
            type="email"
            value={editEmail}
            onChange={(e) => setEditEmail(e.target.value)}
            disabled={editSubmitting}
            placeholder="email@example.com"
            helperText="They can forward suspicious emails to check@secondclear.com"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditClient(null)} disabled={editSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleEditSubmit} variant="contained" disabled={editSubmitting}>
            {editSubmitting ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upgrade Confirmation Dialog */}
      <Dialog open={!!upgradeInfo} onClose={handleUpgradeCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{upgradeInfo?.isOverride ? 'Confirm Adding Person' : 'Confirm Subscription Upgrade'}</DialogTitle>
        <DialogContent>
          {upgradeInfo && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                {upgradeInfo.isOverride
                  ? 'Adding a person will use one of your account slots.'
                  : 'You are currently at your subscription limit.'}
              </Alert>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {upgradeInfo.message}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f7fa' }}>
                {upgradeInfo.isOverride ? (
                  <Box display="flex" justifyContent="space-between">
                    <Typography color="text.secondary">Slots used:</Typography>
                    <Typography fontWeight="bold" color="primary.main">
                      {upgradeInfo.usedSlots + 1} of {upgradeInfo.overrideSlots}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="text.secondary">Current plan:</Typography>
                      <Typography fontWeight="bold">
                        {upgradeInfo.currentQuantity} {upgradeInfo.currentQuantity === 1 ? 'person' : 'people'} — ${upgradeInfo.currentMonthly}/month
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="space-between">
                      <Typography color="text.secondary">New plan:</Typography>
                      <Typography fontWeight="bold" color="primary.main">
                        {upgradeInfo.newQuantity} {upgradeInfo.newQuantity === 1 ? 'person' : 'people'} — ${upgradeInfo.newMonthly}/month
                      </Typography>
                    </Box>
                  </>
                )}
              </Paper>
              {formError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {formError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUpgradeCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleUpgradeConfirm} variant="contained" color="primary" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Confirm & Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Self Upgrade Confirmation Dialog */}
      <Dialog open={!!selfUpgradeInfo} onClose={() => setSelfUpgradeInfo(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Subscription Upgrade</DialogTitle>
        <DialogContent>
          {selfUpgradeInfo && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                You are currently at your subscription limit. Adding yourself requires an additional slot.
              </Alert>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selfUpgradeInfo.message}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f7fa' }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography color="text.secondary">Current plan:</Typography>
                  <Typography fontWeight="bold">
                    {selfUpgradeInfo.currentQuantity} {selfUpgradeInfo.currentQuantity === 1 ? 'person' : 'people'} — ${selfUpgradeInfo.currentMonthly}/month
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">New plan:</Typography>
                  <Typography fontWeight="bold" color="primary.main">
                    {selfUpgradeInfo.newQuantity} {selfUpgradeInfo.newQuantity === 1 ? 'person' : 'people'} — ${selfUpgradeInfo.newMonthly}/month
                  </Typography>
                </Box>
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelfUpgradeInfo(null)} disabled={addingSelf}>
            Cancel
          </Button>
          <Button onClick={() => handleAddSelf(true)} variant="contained" color="primary" disabled={addingSelf}>
            {addingSelf ? <CircularProgress size={24} /> : 'Confirm & Add Yourself'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Self Confirmation Dialog */}
      <Dialog open={removeSelfConfirm || !!selfDowngradeInfo} onClose={() => { setRemoveSelfConfirm(false); setSelfDowngradeInfo(null); }}>
        <DialogTitle>{selfDowngradeInfo ? 'Confirm Subscription Change' : 'Remove yourself?'}</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to stop using Second Clear for yourself?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You will go back to receiving demo responses when you text the service.
          </Typography>
          {selfDowngradeInfo ? (
            <>
              <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
                {selfDowngradeInfo.message}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f7fa' }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography color="text.secondary">Current plan:</Typography>
                  <Typography fontWeight="bold">
                    {selfDowngradeInfo.currentQuantity} {selfDowngradeInfo.currentQuantity === 1 ? 'person' : 'people'} — ${selfDowngradeInfo.currentMonthly}/month
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">New plan:</Typography>
                  <Typography fontWeight="bold" color="success.main">
                    {selfDowngradeInfo.newQuantity} {selfDowngradeInfo.newQuantity === 1 ? 'person' : 'people'} — ${selfDowngradeInfo.newMonthly}/month
                  </Typography>
                </Box>
              </Paper>
            </>
          ) : subscription && subscription.quantity > 1 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Your subscription may be adjusted and you'll receive a prorated credit.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRemoveSelfConfirm(false); setSelfDowngradeInfo(null); }} disabled={removingSelf}>
            Cancel
          </Button>
          {selfDowngradeInfo ? (
            <Button onClick={() => handleRemoveSelf(true)} color="error" variant="contained" disabled={removingSelf}>
              {removingSelf ? <CircularProgress size={24} /> : 'Confirm & Remove'}
            </Button>
          ) : (
            <Button onClick={() => handleRemoveSelf(false)} color="error" variant="contained" disabled={removingSelf}>
              {removingSelf ? <CircularProgress size={24} /> : 'Remove Myself'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default People;
